// Process-level navigation. A detail header replaces these tabs while inspecting evidence.
import React from "react";
import { useTranslation } from "react-i18next";

import type { CaseSubset } from "./processCaseNotes";

export const PROCESS_PANEL_TABS = ["overview", "cases", "steps", "apps"] as const;

export type ProcessPanelTab = (typeof PROCESS_PANEL_TABS)[number];

/** What another tab asks the Cases tab to open on: a quick subset, or an explicit list of cases. */
export type CasesIntent = { subset: CaseSubset } | { ids: string[]; label: string };

/** Moves the panel to a tab, with what that tab should open on where it takes one. */
export type OpenTab = {
  (tab: "cases", intent?: CasesIntent): void;
  (tab: ProcessPanelTab): void;
};

export const ProcessPanelTabBar = ({
  tab,
  onTabChange,
  counts,
}: {
  tab: ProcessPanelTab;
  onTabChange: (tab: ProcessPanelTab) => void;
  /** Shown beside the tab name, where a count says how much the tab holds. */
  counts: Partial<Record<ProcessPanelTab, number>>;
}) => {
  const { t } = useTranslation();
  return (
    <div
      role="tablist"
      aria-label={t("processPanel.tabsLabel")}
      className="flex min-w-0 flex-1 items-stretch gap-0.5 overflow-x-auto px-2 [scrollbar-width:none]"
    >
      {PROCESS_PANEL_TABS.map(id => {
        const selected = id === tab;
        const count = counts[id];
        return (
          <button
            key={id}
            type="button"
            role="tab"
            aria-selected={selected}
            // The tours step back to the panel's landing face through this anchor.
            data-tour={
              id === "overview"
                ? "process-overview-tab"
                : id === "cases"
                  ? "process-cases-tab"
                  : undefined
            }
            onClick={() => onTabChange(id)}
            className={`relative inline-flex shrink-0 items-center gap-1 whitespace-nowrap px-2.5 text-sm font-medium transition focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary/30 ${
              selected ? "text-text" : "text-text-dim hover:text-text"
            }`}
          >
            {t(`processPanel.tab_${id}`)}
            {/* The space is dropped by the flex layout, and the badge reads "(2)" to assistive
                tech, so the accessible name stays "Cases (2)". */}
            {count !== undefined && (
              <>
                {" "}
                <span
                  className={`ml-0.5 inline-flex h-5 min-w-5 items-center justify-center rounded-full border border-outline bg-surface px-1.5 text-[11px] font-medium tabular-nums ${
                    selected ? "text-text" : "text-text-dim"
                  }`}
                >
                  <span className="sr-only">({count})</span>
                  <span aria-hidden="true">{count}</span>
                </span>
              </>
            )}
            <span
              aria-hidden="true"
              className={`absolute inset-x-1.5 bottom-0 h-0.5 rounded-full ${
                selected ? "bg-text" : "bg-transparent"
              }`}
            />
          </button>
        );
      })}
    </div>
  );
};
